import React, { useEffect, useRef, useState } from "react";
import "./NetworkGraph.scss";
import ComboBox from "../Shared/Widgets/Autocomplete/Autocomplete";
import {
  useLazyGetAwardsBySearchStringQuery,
  useLazyGetNetworkGraphCountsQuery,
  useLazyGetNetworkGraphAwardeesQuery,
} from "../../services/rtkQueryServices/checkApiService";
import { Box, Button, Card, Typography } from "@mui/material";
import { Network } from "vis-network/standalone/esm/vis-network";
import Modal from "../Shared/Widgets/Modal/Modal";
import Slider from "../Shared/Widgets/Slider/Slider";
import NetworkGraphAwardees from "../NetworkGraphAwardees/Awardees";

const nodeColors = ["#2563EB", "#DB2777", "#059669", "#F59E0B"];

const fmt = (n) => (n == null ? "-" : Number(n).toLocaleString());

const NetworkGraph = () => {
  const [allAwards, setAllAwards] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openSlider, setOpenSlider] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [selectedAwards, setSelectedAwards] = useState([
    { id: 5993, label: "Member National Academy of Engineering" },
    { id: 5994, label: "Fellow National Academy of Inventors" },
    { id: 419, label: "The National Medal of Technology and Innovation" },
  ]);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState([[], [], []]);
  // The award set + counts the visible graph was built from, stored together
  // so a dropdown change can never pair new awards with stale counts.
  const [built, setBuilt] = useState({ awards: [], counts: null });
  const buildReq = useRef(0);
  const [triggerFetchAwards, { data: awardsData, isSuccess }] =
    useLazyGetAwardsBySearchStringQuery();
  const [triggerFetchGraphData, { isFetching: countsFetching }] =
    useLazyGetNetworkGraphCountsQuery();
  const [triggerFetchAwardees, { data: awardeesData }] =
    useLazyGetNetworkGraphAwardeesQuery();
  const networkContainerRef = useRef(null);
  const networkRef = useRef(null);

  const fetchAwards = async (query) => {
    try {
      triggerFetchAwards(query);
    } catch (error) {
      console.error("Error triggering awards fetch:", error);
    }
  };

  useEffect(() => {
    if (isSuccess && awardsData?.data) {
      setAllAwards(awardsData.data);
    }
  }, [awardsData, isSuccess]);

  useEffect(() => {
    if (awardeesData && awardeesData.length) {
      if (awardeesData.length > 50) {
        requestAnimationFrame(() => setIsModalOpen(true));
      } else {
        requestAnimationFrame(() => setOpenSlider(true));
      }
    }
  }, [awardeesData, isClicked]);

  const filterAwards = (selectedAwards, currentAward) =>
    allAwards.filter(
      (award) =>
        !selectedAwards.some(
          (selected) => selected && selected.id === award.id
        ) ||
        (currentAward && currentAward.id === award.id)
    );

  useEffect(() => {
    const filteredOptions = selectedAwards.map((selectedAward, index) =>
      filterAwards(
        selectedAwards.filter((_, i) => i !== index),
        selectedAward
      )
    );
    setAutoCompleteOptions(filteredOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAwards, allAwards]);

  const handleAwardSelect = (award, index) => {
    const updatedAwards = [...selectedAwards];
    updatedAwards[index] = award;
    setSelectedAwards(updatedAwards);
  };

  const handleBuildClick = () => {
    const validAwards = selectedAwards.filter(Boolean);
    if (validAwards.length < 2) return;
    const req = ++buildReq.current;
    triggerFetchGraphData(validAwards.map((award) => award.id)).then(
      (result) => {
        // Ignore out-of-order responses from rapid rebuilds.
        if (req === buildReq.current && result.data) {
          setBuilt({ awards: validAwards, counts: result.data });
        }
      }
    );
  };

  const handleGraphClick = (data) => {
    const ids = data?.map((item) => item.id);
    setCurrentData(data);
    setIsClicked(!isClicked);
    triggerFetchAwardees(ids);
  };

  useEffect(() => {
    const { awards, counts } = built;
    if (!counts || awards.length < 2) return;

    const nodes = awards.map((award, index) => ({
      id: award.id,
      label: fmt(counts.individual_award_counts?.[award.id]),
      color: nodeColors[index],
    }));

    nodes.push({
      id: "central_node",
      label: `Won ${awards.length === 2 ? "both" : `all ${awards.length}`}: ${
        counts.total_common_across_all
      }`,
      color: { background: nodeColors[3], border: "#1f2a37" },
      shape: "box",
      margin: 10,
      font: { color: "#1f2a37" },
    });

    const edges = [];
    awards.forEach((award1, i) => {
      awards.slice(i + 1).forEach((award2) => {
        edges.push({
          from: award1.id,
          to: award2.id,
          label: `${
            counts.common_between_awards[
              `common_award_${award1.id}_and_award_${award2.id}`
            ]
          }`,
          data: [award1, award2],
        });
      });
    });

    awards.forEach((award) => {
      edges.push({
        from: award.id,
        to: "central_node",
        dashes: true,
        data: awards,
      });
    });

    const options = {
      nodes: {
        shape: "dot",
        size: 26,
        borderWidth: 2,
        font: { size: 14, color: "#334155" },
      },
      edges: {
        font: {
          align: "middle",
          size: 13,
          color: "#475569",
          strokeWidth: 5,
          strokeColor: "#ffffff",
        },
        color: { color: "#94a3b8", highlight: "#334155" },
        width: 1.5,
      },
      layout: { improvedLayout: true },
      physics: { stabilization: true },
      interaction: { hover: true },
    };

    requestAnimationFrame(() => {
      if (!networkContainerRef.current) return;
      networkRef.current?.destroy();
      networkRef.current = new Network(
        networkContainerRef.current,
        { nodes, edges },
        options
      );

      networkRef.current.on("click", function (params) {
        if (params.edges.length > 0) {
          const edgeId = params.edges[0];
          const edge = edges.find((edge) => edge.id === edgeId);
          if (
            edge &&
            edge.data &&
            edge.label !== "0" &&
            edge.label !== "undefined"
          ) {
            handleGraphClick(edge.data);
          }
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [built]);

  useEffect(() => {
    // Preload the browse list for the dropdowns (the client filters locally),
    // then build the default example graph.
    fetchAwards("");
    handleBuildClick();
    return () => networkRef.current?.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-3 h-100 overflow-auto network-landing">
      {/* HERO */}
      <Box
        className="network-hero"
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: "16px",
          p: { xs: 3, md: 4 },
          mb: 3,
          color: "#fff",
          background: (t) =>
            `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
          boxShadow: "0 10px 30px rgba(16,24,40,0.12)",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -60,
            right: -40,
            width: 220,
            height: 220,
            borderRadius: "50%",
            bgcolor: "secondary.main",
            opacity: 0.18,
          }}
        />
        <Box sx={{ position: "relative" }}>
          <Typography
            variant="overline"
            sx={{ letterSpacing: "0.18em", fontWeight: 700, color: "secondary.main" }}
          >
            Award pathways
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, mt: 0.5, mb: 1.5, maxWidth: 720, lineHeight: 1.15 }}
          >
            Correlated awards
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 640 }}>
            Pick two or three major awards and see how often they appear in the
            same career - and who those scholars are.
          </Typography>
        </Box>
      </Box>

      {/* CONTROLS */}
      <Card variant="outlined" className="network-controls-card">
        <Typography variant="subtitle2" sx={{ fontWeight: 700, opacity: 0.7 }}>
          Compare awards
        </Typography>
        <div className="awards-dropdown-inputs">
          {selectedAwards.map((selectedAward, index) => (
            <div key={index} className="awards-dropdown-cell">
              <ComboBox
                autoCompleteOptions={autoCompleteOptions[index]}
                autoCompleteLabel={`Award ${index + 1}`}
                onSelect={(award) => handleAwardSelect(award, index)}
                value={selectedAward}
                width="100%"
              />
            </div>
          ))}
          <Button
            variant="contained"
            color="primary"
            className="network-search-btn"
            onClick={handleBuildClick}
            disabled={selectedAwards.filter(Boolean).length < 2 || countsFetching}
          >
            Build graph
          </Button>
        </div>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Listing awards with at least 50 recorded recipients - pick at least
          two to compare.
        </Typography>
      </Card>

      {/* GRAPH */}
      <Card variant="outlined" className="network-graph-card">
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, opacity: 0.7 }}>
          Shared recipients
        </Typography>
        {built.counts && (
          <div className="network-legend">
            {built.awards.map((award, index) => (
              <span key={award.id} className="network-legend-item">
                <span
                  className="dot"
                  style={{ backgroundColor: nodeColors[index] }}
                />
                <b>{fmt(built.counts.individual_award_counts?.[award.id])}</b>
                {award.label}
              </span>
            ))}
            <span className="network-legend-item">
              <span className="dot" style={{ backgroundColor: nodeColors[3] }} />
              <b>{fmt(built.counts.total_common_across_all)}</b>
              won {built.awards.length === 2 ? "both" : `all ${built.awards.length}`}
            </span>
          </div>
        )}
        <div ref={networkContainerRef} className="network-graph" />
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, color: "text.secondary" }}
        >
          Numbers on the connections count scholars who hold both awards -
          click a connection to see who they are.
        </Typography>
      </Card>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={"Awardees"}
      >
        <NetworkGraphAwardees
          currentData={currentData}
          awardeesData={awardeesData}
        />
      </Modal>
      <Slider isOpen={openSlider} onClose={() => setOpenSlider(false)}>
        <NetworkGraphAwardees
          currentData={currentData}
          awardeesData={awardeesData}
        />
      </Slider>
    </div>
  );
};

export default NetworkGraph;
